<template>
  <!-- Dashboard Normal -->
  <div v-if="currentView === 'dashboard' && !kioskMode" class="w-full overflow-x-hidden">
    <div :class="[
      'p-3 sm:p-4 md:p-5 lg:p-6 min-h-screen transition-all duration-300',
      themeClasses.background
    ]">
      
      <!-- Header do Dashboard -->
      <div class="mb-6 sm:mb-7 md:mb-8">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div class="text-center sm:text-left">
            <h1 :class="[
              'text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-2 transition-colors duration-300',
              themeClasses.primaryText
            ]">
              Dashboard Executivo
            </h1>
            <p :class="[
              'text-sm sm:text-base md:text-lg font-medium transition-colors duration-300',
              themeClasses.secondaryText
            ]">
              Visão geral em tempo real do sistema
            </p>
          </div>
          <div class="flex flex-col sm:flex-row items-center gap-3">
            <div :class="[
              'text-sm sm:text-base text-center sm:text-right px-4 py-2 rounded-lg shadow-md border transition-all duration-300',
              themeClasses.cardBackground,
              themeClasses.border
            ]">
              <span :class="['block sm:inline font-medium', themeClasses.secondaryText]">Última atualização:</span>
              <span :class="['block sm:inline font-bold ml-2', themeClasses.primaryText]">{{ lastUpdate || 'Nunca' }}</span>
            </div>
            
            <!-- Botão Modo Quiosque - Apenas Desktop -->
            <button 
              v-if="!isMobile"
              @click="enterKioskMode" 
              class="flex items-center justify-center space-x-3 px-5 py-3 text-base font-bold bg-gradient-to-r from-purple-600 via-purple-700 to-purple-800 hover:from-purple-700 hover:via-purple-800 hover:to-purple-900 text-white rounded-xl shadow-xl transition-all duration-300 transform hover:scale-105 w-full sm:w-auto border-2 border-purple-500"
              title="Entrar no modo quiosque (tela cheia)"
            >
              <Monitor class="w-5 h-5" />
              <span>Modo TV/Quiosque</span>
            </button>
            
            <!-- Botão Atualizar -->
            <button 
              @click="loadMetrics" 
              :disabled="loadingMetrics"
              class="flex items-center justify-center space-x-3 px-5 py-3 text-base font-bold bg-gradient-to-r from-emerald-600 via-emerald-700 to-emerald-800 hover:from-emerald-700 hover:via-emerald-800 hover:to-emerald-900 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl shadow-xl transition-all duration-300 transform hover:scale-105 w-full sm:w-auto border-2 border-emerald-500"
            >
              <RefreshCw :class="{ 'animate-spin': loadingMetrics }" class="w-5 h-5" />
              <span>{{ loadingMetrics ? 'Atualizando...' : 'Atualizar Dados' }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Cards de Métricas Principais - Melhorados -->
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 md:gap-6 mb-6 sm:mb-7 md:mb-8">
        <!-- Total de Chamados -->
        <div :class="[
          'rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 p-4 sm:p-5 md:p-6 border-2 transform hover:scale-105',
          isDarkMode 
            ? 'bg-gradient-to-br from-slate-800 via-blue-900/30 to-blue-800/40 border-blue-700/50' 
            : 'bg-gradient-to-br from-white via-blue-50 to-blue-100 border-blue-200'
        ]">
          <div class="flex items-center justify-between">
            <div class="flex-1 min-w-0">
              <p :class="[
                'text-sm sm:text-base md:text-lg font-bold mb-2 truncate transition-colors duration-300',
                isDarkMode ? 'text-blue-300' : 'text-blue-700'
              ]">Total de Chamados</p>
              <p :class="[
                'text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-2 transition-colors duration-300',
                themeClasses.primaryText
              ]">
                {{ metrics.total_chamados }}
              </p>
              <div class="flex items-center">
                <TrendingUp class="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 mr-2 flex-shrink-0" />
                <span :class="[
                  'text-sm sm:text-base font-bold transition-colors duration-300',
                  isDarkMode ? 'text-emerald-400' : 'text-emerald-700'
                ]">+12% este mês</span>
              </div>
            </div>
            <div :class="[
              'p-3 sm:p-4 rounded-2xl flex-shrink-0 ml-3 transition-colors duration-300',
              isDarkMode ? 'bg-blue-800/60' : 'bg-blue-200'
            ]">
              <Ticket :class="[
                'w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 transition-colors duration-300',
                isDarkMode ? 'text-blue-300' : 'text-blue-700'
              ]" />
            </div>
          </div>
        </div>

        <!-- Não Atribuídos -->
        <div :class="[
          'rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 p-4 sm:p-5 md:p-6 border-2 transform hover:scale-105',
          isDarkMode 
            ? 'bg-gradient-to-br from-slate-800 via-orange-900/30 to-orange-800/40 border-orange-700/50' 
            : 'bg-gradient-to-br from-white via-orange-50 to-orange-100 border-orange-200'
        ]">
          <div class="flex items-center justify-between">
            <div class="flex-1 min-w-0">
              <p :class="[
                'text-sm sm:text-base md:text-lg font-bold mb-2 truncate transition-colors duration-300',
                isDarkMode ? 'text-orange-300' : 'text-orange-700'
              ]">Não Atribuídos</p>
              <p :class="[
                'text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-2 transition-colors duration-300',
                themeClasses.primaryText
              ]">
                {{ metrics.chamados_nao_atribuido }}
              </p>
              <div class="flex items-center">
                <AlertCircle class="w-4 h-4 sm:w-5 sm:h-5 text-red-600 mr-2 flex-shrink-0" />
                <span :class="[
                  'text-sm sm:text-base font-bold transition-colors duration-300',
                  isDarkMode ? 'text-red-400' : 'text-red-700'
                ]">Requer Atenção</span>
              </div>
            </div>
            <div :class="[
              'p-3 sm:p-4 rounded-2xl flex-shrink-0 ml-3 transition-colors duration-300',
              isDarkMode ? 'bg-orange-800/60' : 'bg-orange-200'
            ]">
              <UserX :class="[
                'w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 transition-colors duration-300',
                isDarkMode ? 'text-orange-300' : 'text-orange-700'
              ]" />
            </div>
          </div>
        </div>

        <!-- Em Andamento -->
        <div :class="[
          'rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 p-4 sm:p-5 md:p-6 border-2 transform hover:scale-105',
          isDarkMode 
            ? 'bg-gradient-to-br from-slate-800 via-yellow-900/30 to-yellow-800/40 border-yellow-700/50' 
            : 'bg-gradient-to-br from-white via-yellow-50 to-yellow-100 border-yellow-200'
        ]">
          <div class="flex items-center justify-between">
            <div class="flex-1 min-w-0">
              <p :class="[
                'text-sm sm:text-base md:text-lg font-bold mb-2 truncate transition-colors duration-300',
                isDarkMode ? 'text-yellow-300' : 'text-yellow-700'
              ]">Em Andamento</p>
              <p :class="[
                'text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-2 transition-colors duration-300',
                themeClasses.primaryText
              ]">
                {{ metrics.chamados_em_andamento }}
              </p>
              <div class="flex items-center">
                <Clock class="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 mr-2 flex-shrink-0" />
                <span :class="[
                  'text-sm sm:text-base font-bold transition-colors duration-300',
                  isDarkMode ? 'text-amber-400' : 'text-amber-700'
                ]">Em Progresso</span>
              </div>
            </div>
            <div :class="[
              'p-3 sm:p-4 rounded-2xl flex-shrink-0 ml-3 transition-colors duration-300',
              isDarkMode ? 'bg-yellow-800/60' : 'bg-yellow-200'
            ]">
              <Clock :class="[
                'w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 transition-colors duration-300',
                isDarkMode ? 'text-yellow-300' : 'text-yellow-700'
              ]" />
            </div>
          </div>
        </div>

        <!-- Resolvidos -->
        <div :class="[
          'rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 p-4 sm:p-5 md:p-6 border-2 transform hover:scale-105',
          isDarkMode 
            ? 'bg-gradient-to-br from-slate-800 via-emerald-900/30 to-emerald-800/40 border-emerald-700/50' 
            : 'bg-gradient-to-br from-white via-emerald-50 to-emerald-100 border-emerald-200'
        ]">
          <div class="flex items-center justify-between">
            <div class="flex-1 min-w-0">
              <p :class="[
                'text-sm sm:text-base md:text-lg font-bold mb-2 truncate transition-colors duration-300',
                isDarkMode ? 'text-emerald-300' : 'text-emerald-700'
              ]">Resolvidos</p>
              <p :class="[
                'text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-2 transition-colors duration-300',
                themeClasses.primaryText
              ]">
                {{ metrics.chamados_resolvidos }}
              </p>
              <div class="flex items-center">
                <CheckCircle class="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 mr-2 flex-shrink-0" />
                <span :class="[
                  'text-sm sm:text-base font-bold transition-colors duration-300',
                  isDarkMode ? 'text-emerald-400' : 'text-emerald-700'
                ]">Concluídos</span>
              </div>
            </div>
            <div :class="[
              'p-3 sm:p-4 rounded-2xl flex-shrink-0 ml-3 transition-colors duration-300',
              isDarkMode ? 'bg-emerald-800/60' : 'bg-emerald-200'
            ]">
              <CheckCircle :class="[
                'w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 transition-colors duration-300',
                isDarkMode ? 'text-emerald-300' : 'text-emerald-700'
              ]" />
            </div>
          </div>
        </div>
      </div>

      <!-- Gráficos Principais - Melhorados -->
      <div class="grid grid-cols-1 xl:grid-cols-2 gap-6 sm:gap-7 md:gap-8 mb-6 sm:mb-7 md:mb-8">
        <!-- Gráfico de Status -->
        <div :class="[
          'rounded-2xl shadow-2xl p-5 sm:p-6 md:p-7 border-2 transition-all duration-300',
          themeClasses.cardBackground,
          themeClasses.border
        ]">
          <div class="flex items-center justify-between mb-5 sm:mb-6">
            <h3 :class="[
              'text-lg sm:text-xl md:text-2xl lg:text-3xl font-black transition-colors duration-300',
              themeClasses.primaryText
            ]">Status dos Chamados</h3>
            <BarChart3 :class="[
              'w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 transition-colors duration-300',
              isDarkMode ? 'text-blue-400' : 'text-blue-600'
            ]" />
          </div>
          <div class="h-64 sm:h-72 md:h-80 lg:h-96">
            <canvas ref="statusChart"></canvas>
          </div>
          <!-- Legenda personalizada com valores -->
          <div class="mt-6 grid grid-cols-3 gap-3 text-sm sm:text-base">
            <div :class="[
              'flex items-center justify-center p-3 sm:p-4 rounded-xl border-2 transition-all duration-300',
              isDarkMode 
                ? 'bg-orange-900/40 border-orange-700' 
                : 'bg-orange-100 border-orange-300'
            ]">
              <div class="w-4 h-4 bg-orange-600 rounded-full mr-3"></div>
              <div class="text-center">
                <div :class="['font-black text-lg transition-colors duration-300', themeClasses.primaryText]">{{ metrics.chamados_nao_atribuido }}</div>
                <div :class="[
                  'font-bold transition-colors duration-300',
                  isDarkMode ? 'text-orange-300' : 'text-orange-700'
                ]">Não Atrib.</div>
              </div>
            </div>
            <div :class="[
              'flex items-center justify-center p-3 sm:p-4 rounded-xl border-2 transition-all duration-300',
              isDarkMode 
                ? 'bg-yellow-900/40 border-yellow-700' 
                : 'bg-yellow-100 border-yellow-300'
            ]">
              <div class="w-4 h-4 bg-yellow-600 rounded-full mr-3"></div>
              <div class="text-center">
                <div :class="['font-black text-lg transition-colors duration-300', themeClasses.primaryText]">{{ metrics.chamados_em_andamento }}</div>
                <div :class="[
                  'font-bold transition-colors duration-300',
                  isDarkMode ? 'text-yellow-300' : 'text-yellow-700'
                ]">Andamento</div>
              </div>
            </div>
            <div :class="[
              'flex items-center justify-center p-3 sm:p-4 rounded-xl border-2 transition-all duration-300',
              isDarkMode 
                ? 'bg-emerald-900/40 border-emerald-700' 
                : 'bg-emerald-100 border-emerald-300'
            ]">
              <div class="w-4 h-4 bg-emerald-600 rounded-full mr-3"></div>
              <div class="text-center">
                <div :class="['font-black text-lg transition-colors duration-300', themeClasses.primaryText]">{{ metrics.chamados_resolvidos }}</div>
                <div :class="[
                  'font-bold transition-colors duration-300',
                  isDarkMode ? 'text-emerald-300' : 'text-emerald-700'
                ]">Resolvidos</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Gráfico de Tipos -->
        <div :class="[
          'rounded-2xl shadow-2xl p-5 sm:p-6 md:p-7 border-2 transition-all duration-300',
          themeClasses.cardBackground,
          themeClasses.border
        ]">
          <div class="flex items-center justify-between mb-5 sm:mb-6">
            <h3 :class="[
              'text-lg sm:text-xl md:text-2xl lg:text-3xl font-black transition-colors duration-300',
              themeClasses.primaryText
            ]">Tipos de Chamados</h3>
            <PieChart :class="[
              'w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 transition-colors duration-300',
              isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
            ]" />
          </div>
          <div class="h-64 sm:h-72 md:h-80 lg:h-96">
            <canvas ref="typeChart"></canvas>
          </div>
          <!-- Lista de tipos com valores -->
          <div class="mt-6 space-y-2 max-h-32 overflow-y-auto">
            <div v-for="(tipo, typeIndex) in metrics.chamados_por_tipo" :key="tipo.tipo" 
                 :class="[
                   'flex items-center justify-between p-2 sm:p-3 rounded-xl text-sm sm:text-base border transition-all duration-300',
                   isDarkMode 
                     ? 'bg-slate-700/60 border-slate-600' 
                     : 'bg-slate-100 border-slate-200'
                 ]">
              <div class="flex items-center">
                <div class="w-4 h-4 rounded-full mr-3 border-2" 
                     :style="{ 
                       backgroundColor: getTypeColor(typeIndex),
                       borderColor: isDarkMode ? '#1e293b' : '#ffffff'
                     }"></div>
                <span :class="['font-bold truncate transition-colors duration-300', themeClasses.primaryText]">{{ tipo.tipo || 'Não definido' }}</span>
              </div>
              <span :class="['font-black text-lg transition-colors duration-300', themeClasses.primaryText]">{{ tipo.total }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Cards de Tempo Médio - Melhorados -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-7 md:gap-8 mb-6 sm:mb-7 md:mb-8">
        <!-- Tempo Médio de Resposta -->
        <div :class="[
          'rounded-2xl shadow-2xl p-5 sm:p-6 md:p-7 border-2 transition-all duration-300',
          isDarkMode 
            ? 'bg-gradient-to-br from-slate-800 via-purple-900/30 to-purple-800/40 border-purple-700/50' 
            : 'bg-gradient-to-br from-white via-purple-50 to-purple-100 border-purple-200'
        ]">
          <div class="flex items-center justify-between mb-4 sm:mb-5">
            <h3 :class="[
              'text-lg sm:text-xl md:text-2xl font-black transition-colors duration-300',
              themeClasses.primaryText
            ]">Tempo de Resposta</h3>
            <Timer :class="[
              'w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 transition-colors duration-300',
              isDarkMode ? 'text-purple-400' : 'text-purple-600'
            ]" />
          </div>
          <div :class="[
            'text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 transition-colors duration-300',
            isDarkMode ? 'text-purple-300' : 'text-purple-700'
          ]">
            {{ formatHours(metrics.tempo_medio_resposta) }}h
          </div>
          <div :class="[
            'w-full rounded-full h-4 mb-3 border transition-all duration-300',
            isDarkMode 
              ? 'bg-purple-900/50 border-purple-700' 
              : 'bg-purple-200 border-purple-300'
          ]">
            <div 
              class="bg-gradient-to-r from-purple-600 via-purple-700 to-purple-800 h-4 rounded-full transition-all duration-1000 ease-out shadow-lg"
              :style="{ width: `${Math.min((metrics.tempo_medio_resposta / 24) * 100, 100)}%` }"
            ></div>
          </div>
          <p :class="[
            'text-sm sm:text-base font-bold transition-colors duration-300',
            themeClasses.secondaryText
          ]">Meta: 4h | {{ Math.min((metrics.tempo_medio_resposta / 24) * 100, 100).toFixed(1) }}% da meta</p>
        </div>

        <!-- Tempo Médio de Resolução -->
        <div :class="[
          'rounded-2xl shadow-2xl p-5 sm:p-6 md:p-7 border-2 transition-all duration-300',
          isDarkMode 
            ? 'bg-gradient-to-br from-slate-800 via-indigo-900/30 to-indigo-800/40 border-indigo-700/50' 
            : 'bg-gradient-to-br from-white via-indigo-50 to-indigo-100 border-indigo-200'
        ]">
          <div class="flex items-center justify-between mb-4 sm:mb-5">
            <h3 :class="[
              'text-lg sm:text-xl md:text-2xl font-black transition-colors duration-300',
              themeClasses.primaryText
            ]">Tempo de Resolução</h3>
            <CheckCircle :class="[
              'w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 transition-colors duration-300',
              isDarkMode ? 'text-indigo-400' : 'text-indigo-600'
            ]" />
          </div>
          <div :class="[
            'text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 transition-colors duration-300',
            isDarkMode ? 'text-indigo-300' : 'text-indigo-700'
          ]">
            {{ formatHours(metrics.tempo_medio_resolucao) }}h
          </div>
          <div :class="[
            'w-full rounded-full h-4 mb-3 border transition-all duration-300',
            isDarkMode 
              ? 'bg-indigo-900/50 border-indigo-700' 
              : 'bg-indigo-200 border-indigo-300'
          ]">
            <div 
              class="bg-gradient-to-r from-indigo-600 via-indigo-700 to-indigo-800 h-4 rounded-full transition-all duration-1000 ease-out shadow-lg"
              :style="{ width: `${Math.min((metrics.tempo_medio_resolucao / 72) * 100, 100)}%` }"
            ></div>
          </div>
          <p :class="[
            'text-sm sm:text-base font-bold transition-colors duration-300',
            themeClasses.secondaryText
          ]">Meta: 24h | {{ Math.min((metrics.tempo_medio_resolucao / 72) * 100, 100).toFixed(1) }}% da meta</p>
        </div>
      </div>

      <!-- Gráfico de Filas e SLA - Melhorados -->
      <div class="grid grid-cols-1 xl:grid-cols-2 gap-6 sm:gap-7 md:gap-8 mb-8">
        <!-- Chamados por Fila -->
        <div :class="[
          'rounded-2xl shadow-2xl p-5 sm:p-6 md:p-7 border-2 transition-all duration-300',
          themeClasses.cardBackground,
          themeClasses.border
        ]">
          <div class="flex items-center justify-between mb-5 sm:mb-6">
            <h3 :class="[
              'text-lg sm:text-xl md:text-2xl lg:text-3xl font-black transition-colors duration-300',
              themeClasses.primaryText
            ]">Chamados por Fila</h3>
            <BarChart3 :class="[
              'w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 transition-colors duration-300',
              isDarkMode ? 'text-cyan-400' : 'text-cyan-600'
            ]" />
          </div>
          <div class="h-64 sm:h-72 md:h-80 lg:h-96">
            <canvas ref="queueChart"></canvas>
          </div>
          <!-- Lista de filas com valores -->
          <div class="mt-6 space-y-2 max-h-32 overflow-y-auto">
            <div v-for="fila in metrics.chamados_por_fila" :key="fila.fila__nome" 
                 :class="[
                   'flex items-center justify-between p-2 sm:p-3 rounded-xl text-sm sm:text-base border transition-all duration-300',
                   isDarkMode 
                     ? 'bg-slate-700/60 border-slate-600' 
                     : 'bg-slate-100 border-slate-200'
                 ]">
              <div class="flex items-center">
                <div :class="[
                  'w-4 h-4 bg-cyan-600 rounded-full mr-3 border-2',
                  isDarkMode ? 'border-slate-800' : 'border-white'
                ]"></div>
                <span :class="['font-bold truncate transition-colors duration-300', themeClasses.primaryText]">{{ fila.fila__nome || 'Sem fila' }}</span>
              </div>
              <span :class="['font-black text-lg transition-colors duration-300', themeClasses.primaryText]">{{ fila.total }}</span>
            </div>
          </div>
        </div>

        <!-- SLA Performance -->
        <div :class="[
          'rounded-2xl shadow-2xl p-5 sm:p-6 md:p-7 border-2 transition-all duration-300',
          themeClasses.cardBackground,
          themeClasses.border
        ]">
          <div class="flex items-center justify-between mb-5 sm:mb-6">
            <h3 :class="[
              'text-lg sm:text-xl md:text-2xl lg:text-3xl font-black transition-colors duration-300',
              themeClasses.primaryText
            ]">Performance SLA</h3>
            <Target :class="[
              'w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 transition-colors duration-300',
              isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
            ]" />
          </div>
          <div class="space-y-4 max-h-64 sm:max-h-72 md:max-h-80 lg:max-h-96 overflow-y-auto">
            <div v-if="metrics.sla_stats.length === 0" :class="[
              'text-center py-8 transition-colors duration-300',
              isDarkMode ? 'text-slate-400' : 'text-slate-500'
            ]">
              <Target class="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 opacity-50" />
              <p class="text-lg sm:text-xl font-bold">Sem dados SLA disponíveis</p>
            </div>
            <div v-for="sla in metrics.sla_stats" :key="sla.tipo_nome" :class="[
              'rounded-xl p-4 sm:p-5 border transition-all duration-300',
              isDarkMode 
                ? 'bg-slate-700/60 border-slate-600' 
                : 'bg-slate-100 border-slate-200'
            ]">
              <div class="flex items-center justify-between mb-3">
                <span :class="[
                  'font-black text-base sm:text-lg truncate transition-colors duration-300',
                  themeClasses.primaryText
                ]">{{ sla.tipo_nome }}</span>
                <span :class="[
                  'text-sm sm:text-base font-bold px-3 py-1 rounded-lg transition-all duration-300',
                  isDarkMode 
                    ? 'text-slate-300 bg-slate-600' 
                    : 'text-slate-600 bg-slate-200'
                ]">{{ sla.tempo_primeira_resposta }}h</span>
              </div>
              <div class="flex items-center justify-between text-sm sm:text-base mb-3 gap-4">
                <div :class="[
                  'flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-300',
                  isDarkMode 
                    ? 'bg-emerald-900/40' 
                    : 'bg-emerald-100'
                ]">
                  <CheckCircle class="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 flex-shrink-0" />
                  <span :class="[
                    'font-bold transition-colors duration-300',
                    isDarkMode ? 'text-emerald-300' : 'text-emerald-700'
                  ]">Atendidos: {{ sla.sla_atendidos }}</span>
                </div>
                <div :class="[
                  'flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-300',
                  isDarkMode 
                    ? 'bg-red-900/40' 
                    : 'bg-red-100'
                ]">
                  <AlertTriangle class="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0" />
                  <span :class="[
                    'font-bold transition-colors duration-300',
                    isDarkMode ? 'text-red-300' : 'text-red-700'
                  ]">Violados: {{ sla.sla_violados }}</span>
                </div>
              </div>
              <div :class="[
                'w-full rounded-full h-3 mb-2 border transition-all duration-300',
                isDarkMode 
                  ? 'bg-slate-600 border-slate-500' 
                  : 'bg-slate-300 border-slate-400'
              ]">
                <div 
                  class="bg-gradient-to-r from-emerald-600 via-emerald-700 to-emerald-800 h-3 rounded-full transition-all duration-1000 ease-out shadow-lg"
                  :style="{ width: `${getSLAPercentage(sla.sla_atendidos, sla.sla_violados)}%` }"
                ></div>
              </div>
              <p :class="[
                'text-sm sm:text-base text-right font-black transition-colors duration-300',
                themeClasses.secondaryText
              ]">
                {{ getSLAPercentage(sla.sla_atendidos, sla.sla_violados).toFixed(1) }}% de conformidade
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State Overlay -->
    <div v-if="loadingMetrics" class="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
      <div :class="[
        'rounded-2xl p-8 flex items-center space-x-4 border-2 shadow-2xl transition-all duration-300',
        themeClasses.cardBackground,
        themeClasses.border
      ]">
        <RefreshCw class="w-8 h-8 animate-spin text-blue-600" />
        <span :class="['text-xl font-bold transition-colors duration-300', themeClasses.primaryText]">Carregando dados...</span>
      </div>
    </div>
  </div>

  <!-- Modo Quiosque/TV - Otimizado para Televisão -->
  <Teleport to="body" v-if="kioskMode">
    <div class="fixed inset-0 z-[9999] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
      <div class="h-screen flex flex-col p-4 lg:p-6 xl:p-8">
        
        <!-- Header Modo TV -->
        <div class="flex-shrink-0 mb-4 lg:mb-6 flex items-center justify-between bg-slate-800/80 backdrop-blur-sm rounded-2xl p-4 lg:p-6 border-2 border-slate-700">
          <div>
            <h1 class="text-2xl lg:text-3xl xl:text-4xl 2xl:text-5xl font-black text-white mb-2">
              🖥️ Dashboard Executivo - Modo TV
            </h1>
            <p class="text-base lg:text-lg xl:text-xl text-slate-300 font-medium">
              Monitoramento em tempo real para displays e televisões
            </p>
          </div>
          <div class="flex items-center gap-4">
            <div class="text-sm lg:text-base xl:text-lg text-slate-300 bg-slate-700 px-4 py-2 rounded-xl border border-slate-600">
              <span class="font-medium">Atualizado:</span>
              <span class="font-bold text-white ml-2">{{ lastUpdate || 'Nunca' }}</span>
            </div>
            <button 
              @click="loadMetrics" 
              :disabled="loadingMetrics"
              class="flex items-center space-x-2 px-4 py-2 text-sm lg:text-base font-bold bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl transition-all duration-200 border-2 border-emerald-500"
            >
              <RefreshCw :class="{ 'animate-spin': loadingMetrics }" class="w-5 h-5 lg:w-6 lg:h-6" />
              <span>{{ loadingMetrics ? 'Atualizando...' : 'Atualizar' }}</span>
            </button>
            <button 
              @click="exitKioskMode"
              class="flex items-center space-x-2 px-4 py-2 text-sm lg:text-base font-bold bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all duration-200 border-2 border-red-500"
            >
              <X class="w-5 h-5 lg:w-6 lg:h-6" />
              <span>Sair do Modo TV</span>
            </button>
          </div>
        </div>

        <!-- Cards de Métricas - TV Mode -->
        <div class="flex-shrink-0 grid grid-cols-4 gap-4 lg:gap-6 mb-4 lg:mb-6">
          <!-- Total de Chamados -->
          <div class="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl shadow-2xl p-4 lg:p-6 border-2 border-blue-500">
            <div class="flex items-center justify-between">
              <div class="flex-1 min-w-0">
                <p class="text-sm lg:text-base xl:text-lg font-bold text-blue-100 mb-2 truncate">TOTAL</p>
                <p class="text-2xl lg:text-3xl xl:text-4xl 2xl:text-5xl font-black text-white mb-2">
                  {{ metrics.total_chamados }}
                </p>
                <div class="flex items-center">
                  <TrendingUp class="w-4 h-4 lg:w-5 lg:h-5 text-emerald-400 mr-2 flex-shrink-0" />
                  <span class="text-xs lg:text-sm xl:text-base font-bold text-emerald-300">+12%</span>
                </div>
              </div>
              <div class="p-3 lg:p-4 bg-blue-500/50 rounded-2xl flex-shrink-0 ml-3">
                <Ticket class="w-8 h-8 lg:w-10 lg:h-10 xl:w-12 xl:h-12 text-white" />
              </div>
            </div>
          </div>

          <!-- Não Atribuídos -->
          <div class="bg-gradient-to-br from-orange-600 to-orange-800 rounded-2xl shadow-2xl p-4 lg:p-6 border-2 border-orange-500">
            <div class="flex items-center justify-between">
              <div class="flex-1 min-w-0">
                <p class="text-sm lg:text-base xl:text-lg font-bold text-orange-100 mb-2 truncate">NÃO ATRIBUÍDOS</p>
                <p class="text-2xl lg:text-3xl xl:text-4xl 2xl:text-5xl font-black text-white mb-2">
                  {{ metrics.chamados_nao_atribuido }}
                </p>
                <div class="flex items-center">
                  <AlertCircle class="w-4 h-4 lg:w-5 lg:h-5 text-red-400 mr-2 flex-shrink-0" />
                  <span class="text-xs lg:text-sm xl:text-base font-bold text-red-300">ATENÇÃO</span>
                </div>
              </div>
              <div class="p-3 lg:p-4 bg-orange-500/50 rounded-2xl flex-shrink-0 ml-3">
                <UserX class="w-8 h-8 lg:w-10 lg:h-10 xl:w-12 xl:h-12 text-white" />
              </div>
            </div>
          </div>

          <!-- Em Andamento -->
          <div class="bg-gradient-to-br from-yellow-600 to-yellow-800 rounded-2xl shadow-2xl p-4 lg:p-6 border-2 border-yellow-500">
            <div class="flex items-center justify-between">
              <div class="flex-1 min-w-0">
                <p class="text-sm lg:text-base xl:text-lg font-bold text-yellow-100 mb-2 truncate">EM ANDAMENTO</p>
                <p class="text-2xl lg:text-3xl xl:text-4xl 2xl:text-5xl font-black text-white mb-2">
                  {{ metrics.chamados_em_andamento }}
                </p>
                <div class="flex items-center">
                  <Clock class="w-4 h-4 lg:w-5 lg:h-5 text-amber-400 mr-2 flex-shrink-0" />
                  <span class="text-xs lg:text-sm xl:text-base font-bold text-amber-300">ATIVO</span>
                </div>
              </div>
              <div class="p-3 lg:p-4 bg-yellow-500/50 rounded-2xl flex-shrink-0 ml-3">
                <Clock class="w-8 h-8 lg:w-10 lg:h-10 xl:w-12 xl:h-12 text-white" />
              </div>
            </div>
          </div>

          <!-- Resolvidos -->
          <div class="bg-gradient-to-br from-emerald-600 to-emerald-800 rounded-2xl shadow-2xl p-4 lg:p-6 border-2 border-emerald-500">
            <div class="flex items-center justify-between">
              <div class="flex-1 min-w-0">
                <p class="text-sm lg:text-base xl:text-lg font-bold text-emerald-100 mb-2 truncate">RESOLVIDOS</p>
                <p class="text-2xl lg:text-3xl xl:text-4xl 2xl:text-5xl font-black text-white mb-2">
                  {{ metrics.chamados_resolvidos }}
                </p>
                <div class="flex items-center">
                  <CheckCircle class="w-4 h-4 lg:w-5 lg:h-5 text-emerald-400 mr-2 flex-shrink-0" />
                  <span class="text-xs lg:text-sm xl:text-base font-bold text-emerald-300">SUCESSO</span>
                </div>
              </div>
              <div class="p-3 lg:p-4 bg-emerald-500/50 rounded-2xl flex-shrink-0 ml-3">
                <CheckCircle class="w-8 h-8 lg:w-10 lg:h-10 xl:w-12 xl:h-12 text-white" />
              </div>
            </div>
          </div>
        </div>

        <!-- Linha Principal - Gráficos TV Mode -->
        <div class="flex-1 grid grid-cols-12 gap-4 lg:gap-6 min-h-0">
          
          <!-- Gráfico de Status -->
          <div class="col-span-3 flex min-h-0 flex-col rounded-2xl border-2 border-slate-700 bg-slate-800/80 p-4 shadow-2xl backdrop-blur-sm lg:p-6 2xl:p-8">
            <div class="flex items-center justify-between mb-4 flex-shrink-0">
              <h3 class="text-lg font-black text-white lg:text-xl xl:text-2xl 3xl:text-3xl 4xl:text-4xl">STATUS</h3>
              <BarChart3 class="w-6 h-6 lg:w-7 lg:h-7 text-blue-400" />
            </div>
            <div class="relative min-h-[250px] flex-1 xl:min-h-[300px] 2xl:min-h-[350px]">
              <canvas ref="statusChartKiosk" class="absolute inset-0 !h-full !w-full max-h-full max-w-full"></canvas>
            </div>
            <!-- Valores no modo TV -->
            <div class="mt-4 text-sm lg:text-base space-y-2">
              <div class="flex justify-between bg-orange-600/20 p-2 rounded-lg border border-orange-500/30">
                <span class="text-orange-300 font-bold">Não Atrib.:</span>
                <span class="font-black text-white text-lg">{{ metrics.chamados_nao_atribuido }}</span>
              </div>
              <div class="flex justify-between bg-yellow-600/20 p-2 rounded-lg border border-yellow-500/30">
                <span class="text-yellow-300 font-bold">Andamento:</span>
                <span class="font-black text-white text-lg">{{ metrics.chamados_em_andamento }}</span>
              </div>
              <div class="flex justify-between bg-emerald-600/20 p-2 rounded-lg border border-emerald-500/30">
                <span class="text-emerald-300 font-bold">Resolvidos:</span>
                <span class="font-black text-white text-lg">{{ metrics.chamados_resolvidos }}</span>
              </div>
            </div>
          </div>

          <!-- Gráfico de Tipos -->
          <div class="col-span-3 flex min-h-0 flex-col rounded-2xl border-2 border-slate-700 bg-slate-800/80 p-4 shadow-2xl backdrop-blur-sm lg:p-6 2xl:p-8">
            <div class="flex items-center justify-between mb-4 flex-shrink-0">
              <h3 class="text-lg font-black text-white lg:text-xl xl:text-2xl 3xl:text-3xl 4xl:text-4xl">TIPOS</h3>
              <PieChart class="w-6 h-6 lg:w-7 lg:h-7 text-emerald-400" />
            </div>
            <div class="relative min-h-[250px] flex-1 xl:min-h-[300px] 2xl:min-h-[350px]">
              <canvas ref="typeChartKiosk" class="absolute inset-0 !h-full !w-full max-h-full max-w-full"></canvas>
            </div>
            <!-- Valores dos tipos no modo TV -->
            <div class="mt-4 text-sm lg:text-base space-y-2 max-h-24 overflow-y-auto">
              <div v-for="tipo in metrics.chamados_por_tipo.slice(0, 3)" :key="tipo.tipo" 
                   class="flex justify-between bg-slate-700/50 p-2 rounded-lg border border-slate-600">
                <span class="text-slate-300 font-bold truncate">{{ (tipo.tipo || 'Não def.').substring(0, 10) }}:</span>
                <span class="font-black text-white text-lg">{{ tipo.total }}</span>
              </div>
            </div>
          </div>

          <!-- Gráfico de Filas -->
          <div class="col-span-3 flex min-h-0 flex-col rounded-2xl border-2 border-slate-700 bg-slate-800/80 p-4 shadow-2xl backdrop-blur-sm lg:p-6 2xl:p-8">
            <div class="flex items-center justify-between mb-4 flex-shrink-0">
              <h3 class="text-lg font-black text-white lg:text-xl xl:text-2xl 3xl:text-3xl 4xl:text-4xl">FILAS</h3>
              <BarChart3 class="w-6 h-6 lg:w-7 lg:h-7 text-cyan-400" />
            </div>
            <div class="relative min-h-[250px] flex-1 xl:min-h-[300px] 2xl:min-h-[350px]">
              <canvas ref="queueChartKiosk" class="absolute inset-0 !h-full !w-full max-h-full max-w-full"></canvas>
            </div>
            <!-- Valores das filas no modo TV -->
            <div class="mt-4 text-sm lg:text-base space-y-2 max-h-24 overflow-y-auto">
              <div v-for="fila in metrics.chamados_por_fila.slice(0, 3)" :key="fila.fila__nome" 
                   class="flex justify-between bg-slate-700/50 p-2 rounded-lg border border-slate-600">
                <span class="text-slate-300 font-bold truncate">{{ (fila.fila__nome || 'Sem fila').substring(0, 10) }}:</span>
                <span class="font-black text-white text-lg">{{ fila.total }}</span>
              </div>
            </div>
          </div>

          <!-- Painel Lateral - Tempos e SLA -->
          <div class="col-span-3 flex flex-col space-y-4 lg:space-y-6 min-h-0">
            
            <!-- Tempo Médio de Resposta -->
            <div class="flex-1 bg-gradient-to-br from-purple-600 to-purple-800 rounded-2xl shadow-2xl p-4 lg:p-6 border-2 border-purple-500 min-h-0">
              <div class="flex items-center justify-between mb-3">
                <h3 class="text-sm font-black text-white lg:text-base xl:text-lg 3xl:text-xl 4xl:text-2xl">TEMPO RESPOSTA</h3>
                <Timer class="w-5 h-5 lg:w-6 lg:h-6 text-purple-300" />
              </div>
              <div class="text-2xl lg:text-3xl xl:text-4xl font-black text-white mb-3">
                {{ formatHours(metrics.tempo_medio_resposta) }}h
              </div>
              <div class="w-full bg-purple-300/30 rounded-full h-3 mb-2 border border-purple-400">
                <div 
                  class="bg-gradient-to-r from-purple-300 to-white h-3 rounded-full transition-all duration-1000 ease-out shadow-lg"
                  :style="{ width: `${Math.min((metrics.tempo_medio_resposta / 24) * 100, 100)}%` }"
                ></div>
              </div>
              <p class="text-xs lg:text-sm text-purple-200 font-bold">Meta: 4h</p>
            </div>

            <!-- Tempo Médio de Resolução -->
            <div class="flex-1 bg-gradient-to-br from-indigo-600 to-indigo-800 rounded-2xl shadow-2xl p-4 lg:p-6 border-2 border-indigo-500 min-h-0">
              <div class="flex items-center justify-between mb-3">
                <h3 class="text-sm font-black text-white lg:text-base xl:text-lg 3xl:text-xl 4xl:text-2xl">TEMPO RESOLUÇÃO</h3>
                <CheckCircle class="w-5 h-5 lg:w-6 lg:h-6 text-indigo-300" />
              </div>
              <div class="text-2xl lg:text-3xl xl:text-4xl font-black text-white mb-3">
                {{ formatHours(metrics.tempo_medio_resolucao) }}h
              </div>
              <div class="w-full bg-indigo-300/30 rounded-full h-3 mb-2 border border-indigo-400">
                <div 
                  class="bg-gradient-to-r from-indigo-300 to-white h-3 rounded-full transition-all duration-1000 ease-out shadow-lg"
                  :style="{ width: `${Math.min((metrics.tempo_medio_resolucao / 72) * 100, 100)}%` }"
                ></div>
              </div>
              <p class="text-xs lg:text-sm text-indigo-200 font-bold">Meta: 24h</p>
            </div>

            <!-- SLA Performance -->
            <div class="flex-1 bg-slate-800/80 backdrop-blur-sm rounded-2xl shadow-2xl p-4 lg:p-6 border-2 border-slate-700 flex flex-col min-h-0">
              <div class="flex items-center justify-between mb-3 flex-shrink-0">
                <h3 class="text-sm font-black text-white lg:text-base xl:text-lg 3xl:text-xl 4xl:text-2xl">SLA</h3>
                <Target class="w-5 h-5 lg:w-6 lg:h-6 text-emerald-400" />
              </div>
              <div class="flex-1 space-y-2 lg:space-y-3 overflow-y-auto min-h-0">
                <div v-if="metrics.sla_stats.length === 0" class="text-center text-slate-400 py-4">
                  <Target class="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p class="text-xs lg:text-sm font-bold">Sem dados SLA</p>
                </div>
                <div v-for="sla in metrics.sla_stats" :key="sla.tipo_nome" class="bg-slate-700/50 rounded-xl p-3 lg:p-4 flex-shrink-0 border border-slate-600">
                  <div class="flex items-center justify-between mb-2">
                    <span class="font-black text-white text-xs lg:text-sm truncate">{{ sla.tipo_nome }}</span>
                    <span class="text-xs text-slate-300 bg-slate-600 px-2 py-1 rounded">{{ sla.tempo_primeira_resposta }}h</span>
                  </div>
                  <div class="flex items-center justify-between text-xs mb-2 gap-2">
                    <div class="flex items-center space-x-1 bg-emerald-600/20 px-2 py-1 rounded border border-emerald-500/30">
                      <CheckCircle class="w-3 h-3 text-emerald-400 flex-shrink-0" />
                      <span class="text-emerald-300 font-bold">{{ sla.sla_atendidos }}</span>
                    </div>
                    <div class="flex items-center space-x-1 bg-red-600/20 px-2 py-1 rounded border border-red-500/30">
                      <AlertTriangle class="w-3 h-3 text-red-400 flex-shrink-0" />
                      <span class="text-red-300 font-bold">{{ sla.sla_violados }}</span>
                    </div>
                  </div>
                  <div class="w-full bg-slate-600 rounded-full h-2 mb-1 border border-slate-500">
                    <div 
                      class="bg-gradient-to-r from-emerald-500 to-emerald-400 h-2 rounded-full transition-all duration-1000 ease-out shadow-lg"
                      :style="{ width: `${getSLAPercentage(sla.sla_atendidos, sla.sla_violados)}%` }"
                    ></div>
                  </div>
                  <p class="text-xs text-slate-300 text-right font-black">
                    {{ getSLAPercentage(sla.sla_atendidos, sla.sla_violados).toFixed(1) }}%
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Indicador de Saída -->
        <div class="absolute bottom-6 right-6 bg-black/80 backdrop-blur-sm text-white px-4 py-2 rounded-xl text-sm lg:text-base font-bold border border-slate-600">
          Pressione ESC para sair do modo TV
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, onMounted, nextTick, onUnmounted, computed, watch, defineProps } from 'vue'
import { 
  Ticket, 
  UserX, 
  Clock, 
  CheckCircle, 
  Timer, 
  AlertTriangle, 
  RefreshCw,
  TrendingUp,
  AlertCircle,
  BarChart3,
  PieChart,
  Target,
  Monitor,
  X
} from 'lucide-vue-next'
import apiClient from '@/services/authService'
import Chart from 'chart.js/auto'

// Props para controle do tema pelo componente pai
const props = defineProps({
  darkMode: {
    type: Boolean,
    default: false
  }
})

// Computed para o modo escuro baseado na prop
const isDarkMode = computed(() => props.darkMode)

// Classes de tema computadas
const themeClasses = computed(() => ({
  // Backgrounds
  background: isDarkMode.value 
    ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900' 
    : 'bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50',
  
  cardBackground: isDarkMode.value 
    ? 'bg-slate-800' 
    : 'bg-white',
  
  // Textos
  primaryText: isDarkMode.value 
    ? 'text-white' 
    : 'text-slate-900',
  
  secondaryText: isDarkMode.value 
    ? 'text-slate-300' 
    : 'text-slate-600',
  
  // Bordas
  border: isDarkMode.value 
    ? 'border-slate-700' 
    : 'border-slate-200',
  
  // Estados de hover
  hover: isDarkMode.value 
    ? 'hover:bg-slate-700' 
    : 'hover:bg-slate-100'
}))

// Reactive data
const currentView = ref('dashboard')
const loadingMetrics = ref(false)
const lastUpdate = ref('')
const kioskMode = ref(false)

// Chart refs - Normal
const statusChart = ref(null)
const typeChart = ref(null)
const queueChart = ref(null)

// Chart refs - Kiosk
const statusChartKiosk = ref(null)
const typeChartKiosk = ref(null)
const queueChartKiosk = ref(null)

// Chart instances
let statusChartInstance = null
let typeChartInstance = null
let queueChartInstance = null
let statusChartKioskInstance = null
let typeChartKioskInstance = null
let queueChartKioskInstance = null

const metrics = ref({
  total_chamados: 0,
  chamados_nao_atribuido: 0,
  chamados_em_andamento: 0,
  chamados_resolvidos: 0,
  tempo_medio_resposta: 0,
  tempo_medio_resolucao: 0,
  chamados_por_tipo: [],
  chamados_por_fila: [],
  sla_stats: [],
})

// Detectar se é mobile
const isMobile = computed(() => {
  if (typeof window !== 'undefined') {
    return window.innerWidth < 1024 // lg breakpoint
  }
  return false
})

// Methods
const formatHours = (hours) => {
  if (!hours || hours === 0) return '0'
  return hours.toFixed(1)
}

const getSLAPercentage = (atendidos, violados) => {
  const total = atendidos + violados
  if (total === 0) return 0
  return (atendidos / total) * 100
}

// Cores otimizadas para TV e alto contraste
const getTypeColor = (index) => {
  const colors = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
    '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
  ]
  return colors[index % colors.length]
}

// Função para entrar no modo quiosque
const enterKioskMode = async () => {
  try {
    // Entrar em tela cheia
    await document.documentElement.requestFullscreen()
    kioskMode.value = true
    
    // Aguardar o próximo tick para criar os gráficos do quiosque
    await nextTick()
    setTimeout(() => {
      createKioskCharts()
    }, 200)
  } catch (error) {
    console.warn('Fullscreen API não suportada:', error)
    // Mesmo sem fullscreen, ativar o modo quiosque
    kioskMode.value = true
    await nextTick()
    setTimeout(() => {
      createKioskCharts()
    }, 200)
  }
}

// Função para sair do modo quiosque
const exitKioskMode = async () => {
  kioskMode.value = false

  // Sair da tela cheia se estiver ativa
  if (document.fullscreenElement) {
    try {
      await document.exitFullscreen()
    } catch (error) {
      console.warn('Erro ao sair da tela cheia:', error)
    }
  }

  // Destruir gráficos do quiosque
  destroyKioskCharts()

  // Recriar gráficos normais
  await nextTick()
  setTimeout(() => {
    createNormalCharts()
  }, 200)
}

const getChartOptions = (isKiosk = false) => {
  const textColor = isDarkMode.value ? '#e2e8f0' : '#475569'
  const gridColor = isDarkMode.value ? 'rgba(148, 163, 184, 0.3)' : 'rgba(148, 163, 184, 0.2)'

  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: true,
        bodyFont: {
          size: isKiosk ? 18 : 16,
          weight: 'bold'
        },
        titleFont: {
          size: isKiosk ? 20 : 18,
          weight: 'bold'
        },
        backgroundColor: isDarkMode.value ? 'rgba(0, 0, 0, 0.9)' : 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#fff',
        borderWidth: 2,
        cornerRadius: 8,
        padding: 12
      }
    },
    elements: {
      point: {
        radius: isKiosk ? 4 : 5
      },
      line: {
        borderWidth: 4
      },
      arc: {
        borderWidth: 3
      }
    },
    animation: {
      duration: isKiosk ? 800 : 1200
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: gridColor,
          lineWidth: 2
        },
        ticks: {
          font: {
            size: isKiosk ? 16 : 14,
            weight: 'bold'
          },
          color: textColor
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: isKiosk ? 16 : 14,
            weight: 'bold'
          },
          color: textColor,
          maxRotation: 45
        }
      }
    }
  }
}

// Criar gráficos normais
const createNormalCharts = () => {
  createStatusChart()
  createTypeChart()
  createQueueChart()
}

// Criar gráficos do quiosque
const createKioskCharts = () => {
  createStatusChartKiosk()
  createTypeChartKiosk()
  createQueueChartKiosk()
}

// Destruir gráficos do quiosque
const destroyKioskCharts = () => {
  if (statusChartKioskInstance) {
    statusChartKioskInstance.destroy()
    statusChartKioskInstance = null
  }
  if (typeChartKioskInstance) {
    typeChartKioskInstance.destroy()
    typeChartKioskInstance = null
  }
  if (queueChartKioskInstance) {
    queueChartKioskInstance.destroy()
    queueChartKioskInstance = null
  }
}

const createStatusChart = () => {
  if (statusChartInstance) {
    statusChartInstance.destroy()
  }

  if (!statusChart.value) return

  const ctx = statusChart.value.getContext('2d')

  statusChartInstance = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: ['Não Atribuídos', 'Em Andamento', 'Resolvidos'],
      datasets: [{
        data: [
          metrics.value.chamados_nao_atribuido,
          metrics.value.chamados_em_andamento,
          metrics.value.chamados_resolvidos
        ],
        backgroundColor: [
          'rgba(251, 146, 60, 0.9)',
          'rgba(250, 204, 21, 0.9)',
          'rgba(34, 197, 94, 0.9)'
        ],
        borderColor: [
          'rgb(251, 146, 60)',
          'rgb(250, 204, 21)',
          'rgb(34, 197, 94)'
        ],
        borderWidth: 3,
        hoverOffset: 10
      }]
    },
    options: getChartOptions(false)
  })
}

const createStatusChartKiosk = () => {
  if (statusChartKioskInstance) {
    statusChartKioskInstance.destroy()
  }

  if (!statusChartKiosk.value) return

  const ctx = statusChartKiosk.value.getContext('2d')

  statusChartKioskInstance = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: ['Não Atribuídos', 'Em Andamento', 'Resolvidos'],
      datasets: [{
        data: [
          metrics.value.chamados_nao_atribuido,
          metrics.value.chamados_em_andamento,
          metrics.value.chamados_resolvidos
        ],
        backgroundColor: [
          'rgba(251, 146, 60, 0.95)',
          'rgba(250, 204, 21, 0.95)',
          'rgba(34, 197, 94, 0.95)'
        ],
        borderColor: [
          'rgb(251, 146, 60)',
          'rgb(250, 204, 21)',
          'rgb(34, 197, 94)'
        ],
        borderWidth: 4,
        hoverOffset: 8
      }]
    },
    options: getChartOptions(true)
  })
}

const createTypeChart = () => {
  if (typeChartInstance) {
    typeChartInstance.destroy()
  }

  if (!typeChart.value) return

  const ctx = typeChart.value.getContext('2d')
  const labels = metrics.value.chamados_por_tipo.map(t => {
    const label = t.tipo || 'Não definido'
    return label.length > 15 ? label.substring(0, 15) + '...' : label
  })
  const data = metrics.value.chamados_por_tipo.map(t => t.total)
  const backgroundColors = metrics.value.chamados_por_tipo.map((_, index) => getTypeColor(index))

  typeChartInstance = new Chart(ctx, {
    type: 'bar',
    data: {
      labels,
      datasets: [{
        label: 'Chamados',
        data,
        backgroundColor: backgroundColors,
        borderColor: backgroundColors.map(color => color.replace('0.8', '1')),
        borderWidth: 3,
        borderRadius: 8,
        borderSkipped: false,
      }]
    },
    options: {
      ...getChartOptions(false),
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: isDarkMode.value ? 'rgba(148, 163, 184, 0.3)' : 'rgba(148, 163, 184, 0.2)',
            lineWidth: 2
          },
          ticks: {
            font: {
              size: 14,
              weight: 'bold'
            },
            color: isDarkMode.value ? '#e2e8f0' : '#475569'
          }
        },
        x: {
          grid: {
            display: false
          },
          ticks: {
            font: {
              size: 14,
              weight: 'bold'
            },
            color: isDarkMode.value ? '#e2e8f0' : '#475569',
            maxRotation: 45
          }
        }
      }
    }
  })
}

const createTypeChartKiosk = () => {
  if (typeChartKioskInstance) {
    typeChartKioskInstance.destroy()
  }

  if (!typeChartKiosk.value) return

  const ctx = typeChartKiosk.value.getContext('2d')
  const labels = metrics.value.chamados_por_tipo.map(t => {
    const label = t.tipo || 'Não definido'
    return label.length > 10 ? label.substring(0, 10) + '...' : label
  })
  const data = metrics.value.chamados_por_tipo.map(t => t.total)
  const backgroundColors = metrics.value.chamados_por_tipo.map((_, index) => getTypeColor(index))

  typeChartKioskInstance = new Chart(ctx, {
    type: 'bar',
    data: {
      labels,
      datasets: [{
        label: 'Chamados',
        data,
        backgroundColor: backgroundColors,
        borderColor: backgroundColors.map(color => color.replace('0.8', '1')),
        borderWidth: 4,
        borderRadius: 10,
        borderSkipped: false,
      }]
    },
    options: {
      ...getChartOptions(true),
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(148, 163, 184, 0.4)',
            lineWidth: 2
          },
          ticks: {
            font: {
              size: 16,
              weight: 'bold'
            },
            color: '#e2e8f0'
          }
        },
        x: {
          grid: {
            display: false
          },
          ticks: {
            font: {
              size: 16,
              weight: 'bold'
            },
            color: '#e2e8f0',
            maxRotation: 45
          }
        }
      }
    }
  })
}

const createQueueChart = () => {
  if (queueChartInstance) {
    queueChartInstance.destroy()
  }

  if (!queueChart.value) return

  const ctx = queueChart.value.getContext('2d')
  const labels = metrics.value.chamados_por_fila.map(f => {
    const label = f.fila__nome || 'Sem fila'
    return label.length > 20 ? label.substring(0, 20) + '...' : label
  })
  const data = metrics.value.chamados_por_fila.map(f => f.total)

  queueChartInstance = new Chart(ctx, {
    type: 'bar',
    data: {
      labels,
      datasets: [{
        label: 'Chamados',
        data,
        backgroundColor: 'rgba(6, 182, 212, 0.9)',
        borderColor: 'rgb(6, 182, 212)',
        borderWidth: 3,
        borderRadius: 8,
      }]
    },
    options: {
      ...getChartOptions(false),
      indexAxis: 'y',
      scales: {
        x: {
          beginAtZero: true,
          grid: {
            color: isDarkMode.value ? 'rgba(148, 163, 184, 0.3)' : 'rgba(148, 163, 184, 0.2)',
            lineWidth: 2
          },
          ticks: {
            font: {
              size: 14,
              weight: 'bold'
            },
            color: isDarkMode.value ? '#e2e8f0' : '#475569'
          }
        },
        y: {
          grid: {
            display: false
          },
          ticks: {
            font: {
              size: 14,
              weight: 'bold'
            },
            color: isDarkMode.value ? '#e2e8f0' : '#475569'
          }
        }
      }
    }
  })
}

const createQueueChartKiosk = () => {
  if (queueChartKioskInstance) {
    queueChartKioskInstance.destroy()
  }

  if (!queueChartKiosk.value) return

  const ctx = queueChartKiosk.value.getContext('2d')
  const labels = metrics.value.chamados_por_fila.map(f => {
    const label = f.fila__nome || 'Sem fila'
    return label.length > 12 ? label.substring(0, 12) + '...' : label
  })
  const data = metrics.value.chamados_por_fila.map(f => f.total)

  queueChartKioskInstance = new Chart(ctx, {
    type: 'bar',
    data: {
      labels,
      datasets: [{
        label: 'Chamados',
        data,
        backgroundColor: 'rgba(6, 182, 212, 0.95)',
        borderColor: 'rgb(6, 182, 212)',
        borderWidth: 4,
        borderRadius: 10,
      }]
    },
    options: {
      ...getChartOptions(true),
      indexAxis: 'y',
      scales: {
        x: {
          beginAtZero: true,
          grid: {
            color: 'rgba(148, 163, 184, 0.4)',
            lineWidth: 2
          },
          ticks: {
            font: {
              size: 16,
              weight: 'bold'
            },
            color: '#e2e8f0'
          }
        },
        y: {
          grid: {
            display: false
          },
          ticks: {
            font: {
              size: 16,
              weight: 'bold'
            },
            color: '#e2e8f0'
          }
        }
      }
    }
  })
}

const loadMetrics = async () => {
  loadingMetrics.value = true
  try {
    const { data } = await apiClient.get('/api/v1/tickets/metrics')
    metrics.value = data
    lastUpdate.value = new Date().toLocaleTimeString('pt-BR')
    
    await nextTick()
    
    if (kioskMode.value) {
      createKioskCharts()
    } else {
      createNormalCharts()
    }
    
  } catch (error) {
    console.error('Erro ao carregar métricas:', error)
  } finally {
    loadingMetrics.value = false
  }
}

// Handle window resize
const handleResize = () => {
  setTimeout(() => {
    if (kioskMode.value) {
      if (statusChartKioskInstance) statusChartKioskInstance.resize()
      if (typeChartKioskInstance) typeChartKioskInstance.resize()
      if (queueChartKioskInstance) queueChartKioskInstance.resize()
    } else {
      if (statusChartInstance) statusChartInstance.resize()
      if (typeChartInstance) typeChartInstance.resize()
      if (queueChartInstance) queueChartInstance.resize()
    }
  }, 100)
}

// Handle ESC key to exit kiosk mode
const handleKeydown = (event) => {
  if (event.key === 'Escape' && kioskMode.value) {
    exitKioskMode()
  }
}

// Handle fullscreen change events
const handleFullscreenChange = () => {
  // Se saiu da tela cheia mas ainda está em modo quiosque, desativar o modo quiosque
  if (!document.fullscreenElement && kioskMode.value) {
    exitKioskMode()
  }
}

// Lifecycle
onMounted(async () => {
  await loadMetrics()
  window.addEventListener('resize', handleResize)
  window.addEventListener('keydown', handleKeydown)
  document.addEventListener('fullscreenchange', handleFullscreenChange)
})

// Auto-refresh every 5 minutes
const refreshInterval = setInterval(() => {
  if (document.hidden) return // aba em segundo plano: não consome API
  if (currentView.value === 'dashboard') {
    loadMetrics()
  }
}, 5 * 60 * 1000)

// Cleanup
onUnmounted(() => {
  if (statusChartInstance) statusChartInstance.destroy()
  if (typeChartInstance) typeChartInstance.destroy()
  if (queueChartInstance) queueChartInstance.destroy()
  destroyKioskCharts()
  clearInterval(refreshInterval)
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('keydown', handleKeydown)
  document.removeEventListener('fullscreenchange', handleFullscreenChange)
})

// Observar mudanças no modo escuro e recriar gráficos
watch(isDarkMode, async () => {
  await nextTick()
  if (kioskMode.value) {
    createKioskCharts()
  } else {
    createNormalCharts()
  }
}, { immediate: false })
</script>
